import PropTypes from "prop-types";
import { useContext, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import API from "./axios/API";
import { useStepwiseStorage } from "./hooks/useStepwiseStorage";
import { DataContext } from "./providers/DataProviders/DataProvider";
import { useProfileSetting } from "./providers/ProfileSettingContext/ProfileSettingContext";
import { calculateProfileCompletion } from "./utils/profile/calculateProfileCompletion";
import { ManualOrderContext } from "./providers/ManualOrderContext/ManualOrderProvider";
import { useGuidedTour } from "./providers/GuidedTourContext/GuidedTourContext";
import useAuthUtils from "./utils/auth/AuthUtils";

// Higher-order component to wrap routes and check access
const ProtectedRoute = ({ element }) => {
  const { getModulesByRole } = useAuthUtils();


  const { startTour } = useGuidedTour()
  const { setProfileStatus, setLoginCustomerId, loginCustomerId, setIsBetaUser, setUserHasSeenTour, setProfileComplete, setExternalTokensList, setGateway, setFLName, userData, setUserData, metaData, setMetaData, setUserRole } = useContext(DataContext);

  const { setPersonalDetails, setCompanyDetails, setWorkingHours, setInitialPersonalDetails, setInitialCompanyDetails, setInitialWorkingHours } = useProfileSetting()
  const { clearAllFlowData } = useStepwiseStorage()

  // Get roleName from context metaData
  const roleName = metaData?.roles?.[0]?.name;
  const isBetaUserDefined = metaData?.isBetaUser || false;


  const [statusCode, setStatusCode] = useState("");
  const [error, setError] = useState(null); // State to hold error
  const [isInitialized, setIsInitialized] = useState(false);

  // Ref to prevent duplicate API calls
  const isFetchingRef = useRef(false);

  // Helper function to process user data
  const processUserData = (userData) => {
    const calcDtata = calculateProfileCompletion(userData)
    let externalToken = null;
    if (userData.externalTokensList) {
      // Find the payment gateway token
      const paymentGatewayToken = userData.externalTokensList.find(
        token => token.key === "paymentGateway"
      );

      if (paymentGatewayToken) {
        const gatewayType = paymentGatewayToken.value;

        if (gatewayType === "ABZER") {
          // For ABZER, find the documentNumber token
          const subscriptionToken = userData.externalTokensList.find(
            token => token.key === "documentNumber"
          );
          if (subscriptionToken) {
            externalToken = subscriptionToken;
          }
        } else if (gatewayType === "PAYPAL") {
          // For PAYPAL, find the billing agreement token
          const billingToken = userData.externalTokensList.find(
            token => token.key === "paypal_billing_agreement_id"
          );
          if (billingToken) {
            externalToken = billingToken;
          }
        } else if (gatewayType === "STRIPE") {
          // For STRIPE, find the customer id token
          const stripeToken = userData.externalTokensList.find(
            token => token.key === "stripe_customer_id"
          );
          if (stripeToken) {
            externalToken = stripeToken;
          }
        } else {
          // For other gateways, find any non-gateway token
          const otherToken = userData.externalTokensList.find(
            token => token.key !== "paymentGateway"
          );
          if (otherToken) {
            externalToken = otherToken;
          }
        }
      }
    }


    setExternalTokensList(externalToken);
    setGateway(userData.paymentGateway);
    setFLName(userData.firstName + " " + userData.lastName)
    const personalDetails = {
      customerId: userData.customerId,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      whatsApp: userData.whatsApp,
      slack: userData.slack,
      phone: userData.phone,
      jobTitle: userData.jobTitle,
      isEmailVerified: userData.isEmailVerified,
      isTermsAccepted: userData.isTermsAccepted,
      referralCode: Array.isArray(userData?.internalRefCodes) && userData.internalRefCodes.length > 0 ? userData.internalRefCodes[0].value : "", // Ensure referralCode is defined
      profilePicture: userData.profilePicture || "", // Map profilePicture from API response
      supportMethod: userData.whatsApp ? 'whatsApp' : (userData.slack ? 'slack' : ''), // Set support method based on existing data
    };

    const companyDetails = userData.address ? { companyName: userData?.companyName, companyWebsite: userData?.companyWebsite, addressLine1: userData.address?.addressLine1, addressLine2: userData?.address.addressLine2, city: userData?.address?.city, state: userData?.address?.state, zipCode: userData?.address?.zipCode, country: userData.address?.country, taxId: userData?.taxId, gstNumber: userData.gstNumber } : {
      companyName: "",
      companyWebsite: "",
      addressLine1: "",
      addressLine2: "",
      billingEmailAddress: "",
      city: "",
      state: "",
      zipCode: "",
      country: "", // Default value
      taxId: "",
      gstNumber: ""
    };

    setProfileComplete(calcDtata)
    if (calcDtata.totalPercentageProfileCompleted >= 100) {
      // localStorage.setItem("profileStatus", true)
      setProfileStatus(true);
    } else {
      setProfileStatus(false);
    }

    // Set personal and company details in context
    setPersonalDetails(personalDetails);
    setCompanyDetails(companyDetails);
    setInitialPersonalDetails(personalDetails);
    setInitialCompanyDetails(companyDetails);
    setInitialWorkingHours({
      startTime: userData.workingHours?.start !== undefined && userData.workingHours?.start !== null ? userData.workingHours.start : '',
      endTime: userData.workingHours?.end !== undefined && userData.workingHours?.end !== null ? userData.workingHours.end : '',
      timeZone: userData.workingHours?.timeZone ? userData.workingHours.timeZone : "",
    })
    // console.log(userData?.customerId)
    // console.log(userData.workingHours)

    setWorkingHours({
      startTime: userData.workingHours?.start !== undefined && userData.workingHours?.start !== null ? userData.workingHours.start : '',
      endTime: userData.workingHours?.end !== undefined && userData.workingHours?.end !== null ? userData.workingHours.end : '',
      timeZone: userData.workingHours?.timeZone ? userData.workingHours.timeZone : "",
    })
    setUserHasSeenTour(userData.hasSeenTour)

    setProfileStatus(userData.isProfileComplete);
    localStorage.setItem("loginCustomerId", userData.customerId);
    setLoginCustomerId(userData.customerId);

    // Sync metaData to ensure Sidebar and other components stay updated
    const updatedMetaData = {
      userName: userData.userName || userData.email,
      token: localStorage.getItem("token"),
      roleId: userData.roleId,
      phone: userData?.phone,
      country: userData?.address?.country,
      roleName: userData.roleName,
      roles: userData.roles ? userData.roles.map(r => ({
        id: r.id,
        name: r.name,
        // Ensure modules are always present by using getModulesByRole if response is missing them
        modules: (r.modules && r.modules.length > 0)
          ? r.modules.map(m => ({ id: m.id, name: m.name, moduleType: m.moduleType }))
          : getModulesByRole(r.name)
      })) : (metaData?.roles || []),
      status: 200,
      isBetaUser: userData.betaUser || false,
    };

    localStorage.setItem("metaData", JSON.stringify(updatedMetaData));

    const hasUserRole = userData.roles ? userData.roles.some(r => r.name === "USER") : false;
    const assignedRole = hasUserRole ? "USER" : "ADMIN";
    setUserRole(assignedRole);

    // Batch updates to prevent intermediate unauthorized states
    setMetaData(updatedMetaData);
    setStatusCode(200);
    setIsBetaUser(isBetaUserDefined);

    if (userData.userType === "USER") {
      if (calcDtata.totalPercentageProfileCompleted < 100) {
        setTimeout(() => {
          startTour();
        }, 2000);
      }
    }
  };

  useEffect(() => {
    if (!loginCustomerId) {
      setIsInitialized(true);
      return;
    }

    // Check if user data is already available in context (e.g., from AuthLogin)
    if (userData && userData.customerId && userData.customerId.toString() === loginCustomerId.toString()) {
      // Use existing user data from context
      processUserData(userData);
      setIsInitialized(true);
      return;
    }

    // Prevent duplicate calls
    if (isFetchingRef.current) return;

    const fetchUserDetails = async () => {
      try {
        isFetchingRef.current = true;
        const response = await API.get(
          `${import.meta.env.VITE_BACKEND_API}/getuser`
        );

        const fetchedUserData = response.data;
        // Store user data in context
        setUserData(fetchedUserData);

        processUserData(fetchedUserData);
        setIsInitialized(true);
      } catch (error) {
        console.error("Error fetching Access Response:", error);
        setError(error); // Set error state
        setIsInitialized(true);
      } finally {
        isFetchingRef.current = false;
      }
    };

    fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginCustomerId]); // Fetch details only when loginCustomerId changes

  // Perform access check logic (Defensive, but don't redirect to root to avoid loops)
  let hasRole = false;
  if (metaData && Array.isArray(metaData.roles) && metaData.roles.length > 0) {
    hasRole = true;
  }

  // If no loginCustomerId, redirect to login page
  if (!loginCustomerId) {
    return <Navigate to={`${import.meta.env.VITE_APP_BASE_PATH}/`} />;
  }

  // If there's an error after initialization, handle it
  if (error && isInitialized) {
    console.error("API error in ProtectedRoute:", error);
    clearAllFlowData();
    return <Navigate to={`${import.meta.env.VITE_APP_BASE_PATH}/`} />;
  }

  // If we've initialized but the user has NO roles at all, something is wrong.
  // But don't redirect back to root if we ARE at root.
  if (isInitialized && !hasRole && statusCode === 200) {
    console.warn("User has no roles assigned.");
    // We can just render nothing or a tiny error instead of redirecting
    return null;
  }

  // Render immediately - let the child components handle their own loading states
  return element;
};

export default ProtectedRoute;

ProtectedRoute.propTypes = {
  element: PropTypes.element.isRequired,
};
